import React, { useEffect, useState } from 'react';
import documentsApi from '../services/documentsApi';
import { timeAgo } from '../utils/time';
import Icon from './Icon';
import { DOCUMENT_KIND_LABELS, DOCUMENT_STATUS_LABELS, expiryText, isDriveLink, statusPillText } from './documentUtils';
import './RelationshipManagers.css';
import './CompanyDocuments.css';

const PAGE_SIZE = 100;
const NO_FILTERS = { kind: '', status: '', olderThan: '', expired: false };

/**
 * Every document across companies, filterable by kind and status: "every NDA sent more than
 * 14 days ago with no reply" is kind NDA, status Sent, and a 14 in "Sent over ... days ago".
 */
const DocumentsView = ({ onOpenCompany }) => {
  const [filters, setFilters] = useState(NO_FILTERS);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    documentsApi.list({
      kind: filters.kind,
      status: filters.status,
      older_than_days: filters.olderThan,
      expired: filters.expired ? true : '',
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE
    })
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => { if (!cancelled) { setError(err.message); setData({ total: 0, documents: [] }); } });
    return () => {
      cancelled = true;
    };
  }, [filters, page]);

  const setFilter = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setPage(0);
    setData(null);
    setFilters(current => ({ ...current, [field]: value }));
  };

  const filtered = JSON.stringify(filters) !== JSON.stringify(NO_FILTERS);
  const total = data ? data.total : 0;

  return (
    <div className="documents-view">
      <div className="documents-view__filters">
        <label className="cdoc-field">
          <span>Kind</span>
          <select className="rm-input" value={filters.kind} onChange={setFilter('kind')}>
            <option value="">All kinds</option>
            {Object.entries(DOCUMENT_KIND_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label className="cdoc-field">
          <span>Status</span>
          <select className="rm-input" value={filters.status} onChange={setFilter('status')}>
            <option value="">Any status</option>
            {Object.entries(DOCUMENT_STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label className="cdoc-field">
          <span>Sent over (days ago)</span>
          <input className="rm-input" type="number" min="0" max="36500" value={filters.olderThan}
            onChange={setFilter('olderThan')} placeholder="Any time" />
        </label>
        <label className="documents-view__check">
          <input type="checkbox" checked={filters.expired} onChange={setFilter('expired')} /> Expired only
        </label>
        {filtered && (
          <button type="button" className="rm-button rm-button--quiet" onClick={() => { setPage(0); setFilters(NO_FILTERS); }}>
            <Icon name="x" /> Clear
          </button>
        )}
      </div>

      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {data === null && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}

      {data && data.documents.length === 0 && !error && (
        <p className="placeholder-text">
          {filtered ? 'No documents match.' : 'No documents on file yet. Add one from a company’s panel.'}
        </p>
      )}

      {data && data.documents.length > 0 && (
        <>
          <table className="documents-view__table">
            <thead>
              <tr><th>Company</th><th>Kind</th><th>Title</th><th>Status</th><th>With</th><th>Updated</th><th /></tr>
            </thead>
            <tbody>
              {data.documents.map(document => {
                const expiry = expiryText(document);
                return (
                  <tr key={document.id}>
                    <td>
                      <button type="button" className="rm-link documents-view__company"
                        onClick={() => onOpenCompany?.({ id: document.company_id, name: document.company_name })}>
                        {document.company_name}
                      </button>
                    </td>
                    <td><span className="cdoc-kind">{document.kind_label}</span></td>
                    <td>{document.title}</td>
                    <td>
                      <span className={`cdoc-pill cdoc-pill--${document.status}`}>{statusPillText(document)}</span>
                      {expiry && <span className={`cdoc-badge${document.expired ? ' cdoc-badge--expired' : ''}`}> {expiry}</span>}
                    </td>
                    <td>{document.contact_name || ''}</td>
                    <td>{timeAgo(document.updated_at)}</td>
                    <td>
                      {document.drive_url && isDriveLink(document.drive_url) && (
                        <a href={document.drive_url} target="_blank" rel="noopener noreferrer" className="cdoc-link"
                          aria-label={`Open ${document.title} in Drive`}>
                          <Icon name="external-link" />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="rm-buttons">
            <span className="placeholder-text">{total} {total === 1 ? 'document' : 'documents'}</span>
            {page > 0 && <button type="button" className="rm-button" onClick={() => setPage(page - 1)}>Previous</button>}
            {(page + 1) * PAGE_SIZE < total && (
              <button type="button" className="rm-button" onClick={() => setPage(page + 1)}>Next</button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentsView;
